import { Navigate, Route, Routes as RouterRoutes } from 'react-router-dom';
import {
  activateAccountPath,
  forbiddenPath,
  notFoundPath,
  resendActivationPath,
  resetPasswordLegacyTokenPath,
  resetPasswordPath,
  resetPasswordTokenPath,
  signInPath,
  signUpPath,
  ssoCallbackPath,
  ssoErrorPath,
} from 'common/config/urls';
import { COMPANY_TYPE, FIRM_TYPE, FUND_INVESTMENTS_TYPE, FUND_TYPE, USER_TYPE } from 'common/constants/pageTypes';
import { CompanyContainer as CompanyContainerView, UserContainer as UserContainerView } from 'components';
import { CustomRouteElement } from 'components/CustomRoute/CustomRoute';
import { DASHBOARD_COMPANY_SLUG, DASHBOARD_WORKBOARD_SLUG } from 'dashboard409a/common/constants';
import { DashboardLayout } from 'dashboard409a/layouts';
import { Dashboard as DashboardView } from 'dashboard409a/pages';
import AccountSettingsView from 'dashboard409a/pages/AccountSettings';
import AssignmentRulesView from 'dashboard409a/pages/AssignmentRules';
import { FUND_INVESTMENTS_PATH } from 'pages/FundInvestments/common/urls';
import { FundInvestmentContainer } from 'pages/FundInvestments/components';
import { AdminLayout, AuthLayout, Main as MainLayout, TaskLayout } from './layouts';
import {
  ActivateAccount as ActivateAccountView,
  Firms as FirmsView,
  Funds as FundsView,
  NotFound as NotFoundView,
  ProcessManagement as ProcessManagementView,
  ResendActivation as ResendActivationView,
  ResetPasswordLegacyRedirect as ResetPasswordLegacyRedirectView,
  ResetPassword as ResetPasswordView,
  SetNewPassword as SetNewPasswordView,
  SignIn as SignInView,
  SignUp as SignUpView,
  SsoErrorView,
  SsoExchange as SsoExchangeView,
} from './pages';
import CommonCompanyProfilesPage from './pages/CommonCompanyProfiles';

const Routes = () => (
  <RouterRoutes>
    {/* Root redirect */}
    <Route path="/" element={<Navigate to={signInPath} replace />} />

    {/* Auth */}
    <Route path={signInPath} element={<CustomRouteElement component={SignInView} layout={AuthLayout} />} />
    <Route path={signUpPath} element={<CustomRouteElement component={SignUpView} layout={AuthLayout} />} />
    <Route path={resetPasswordPath} element={<CustomRouteElement component={ResetPasswordView} layout={AuthLayout} />} />
    <Route
      path={resetPasswordTokenPath}
      element={<CustomRouteElement component={SetNewPasswordView} isPrivate={false} layout={AuthLayout} />}
    />
    <Route
      path={resetPasswordLegacyTokenPath}
      element={<CustomRouteElement component={ResetPasswordLegacyRedirectView} isPrivate={false} />}
    />
    <Route
      path={activateAccountPath}
      element={<CustomRouteElement component={ActivateAccountView} layout={AuthLayout} />}
    />
    <Route path={resendActivationPath} element={<CustomRouteElement component={ResendActivationView} layout={AuthLayout} />} />

    {/* SSO */}
    <Route path={ssoErrorPath} element={<CustomRouteElement component={SsoErrorView} layout={AuthLayout} />} />
    <Route path={ssoCallbackPath} element={<SsoExchangeView />} />

    {/* Process Management */}
    <Route
      path="/:processType/requests/:requestId/:hmacToken/:portal?"
      element={<CustomRouteElement component={ProcessManagementView} layout={TaskLayout} />}
    />

    {/* Firms */}
    <Route
      path="/firms/:firmSlugParam/:tableSlugParam"
      element={
        <CustomRouteElement
          component={FirmsView}
          isPrivate
          layout={props => <MainLayout pageType={FIRM_TYPE} {...props} />}
        />
      }
    />

    {/* Funds */}
    <Route
      path="/firms/:firmSlugParam/funds/:fundSlugParam/:tableSlugParam"
      element={
        <CustomRouteElement
          component={FundsView}
          isPrivate
          layout={props => <MainLayout pageType={FUND_TYPE} {...props} />}
        />
      }
    />

    {/* Companies */}
    <Route
      path="/firms/:firmSlugParam/companies/:companySlugParam/:tableSlugParam"
      element={
        <CustomRouteElement
          component={CompanyContainerView}
          isPrivate
          layout={props => <MainLayout pageType={COMPANY_TYPE} {...props} />}
        />
      }
    />

    {/* Fund Investments */}
    <Route
      path={FUND_INVESTMENTS_PATH}
      element={
        <CustomRouteElement
          component={FundInvestmentContainer}
          isPrivate
          layout={props => <MainLayout pageType={FUND_INVESTMENTS_TYPE} {...props} />}
        />
      }
    />

    {/* Users */}
    <Route
      path="/firms/:firmSlugParam/users/:tabSlugParam"
      element={
        <CustomRouteElement
          component={UserContainerView}
          isPrivate
          layout={props => <MainLayout pageType={USER_TYPE} {...props} />}
        />
      }
    />
    <Route
      path="/firms/:firmSlugParam/users/:userIdParam/:tabSlugParam"
      element={
        <CustomRouteElement
          component={UserContainerView}
          isPrivate
          layout={props => <MainLayout pageType={USER_TYPE} {...props} />}
        />
      }
    />

    {/* 409A Dashboard */}
    <Route
      path={`${DASHBOARD_COMPANY_SLUG}/:tableSlugParam`}
      element={
        <CustomRouteElement component={DashboardView} isPrivate layout={props => <DashboardLayout {...props} />} />
      }
    />
    <Route
      path={`${DASHBOARD_COMPANY_SLUG}/:tableSlugParam/:valuationId`}
      element={
        <CustomRouteElement component={DashboardView} isPrivate layout={props => <DashboardLayout {...props} />} />
      }
    />

    {/* Workboard */}
    <Route
      path={`${DASHBOARD_WORKBOARD_SLUG}/account-settings`}
      element={
        <CustomRouteElement
          component={AccountSettingsView}
          isPrivate
          layout={props => <DashboardLayout {...props} />}
        />
      }
    />
    <Route
      path={`${DASHBOARD_WORKBOARD_SLUG}/assignment-rules`}
      element={
        <CustomRouteElement
          component={AssignmentRulesView}
          isPrivate
          layout={props => <DashboardLayout {...props} />}
        />
      }
    />
    <Route
      path={`${DASHBOARD_WORKBOARD_SLUG}/:tabSlugParam`}
      element={
        <CustomRouteElement component={DashboardView} isPrivate layout={props => <DashboardLayout {...props} />} />
      }
    />

    {/* Admin */}
    <Route
      path="/admin/common-company-profiles"
      element={
        <CustomRouteElement
          component={CommonCompanyProfilesPage}
          isPrivate
          layout={props => <AdminLayout {...props} />}
        />
      }
    />

    {/* Shared error routes */}
    <Route path={notFoundPath} element={<CustomRouteElement component={NotFoundView} />} />
    <Route path={forbiddenPath} element={<CustomRouteElement component={NotFoundView} />} />

    {/* Fallback */}
    <Route path="*" element={<Navigate to={notFoundPath} replace />} />
  </RouterRoutes>
);

export default Routes;
